import { useQueryClient } from "@tanstack/react-query";
import { PrivateHireSearchForm } from "@/components/private-hires/PrivateHireSearchForm";
import { PrivateHireSearchResults } from "@/components/private-hires/PrivateHireSearchResults";
import { LastSearchBanner } from "@/components/shared/last-search-banner";
import { PageHeader } from "@/components/shared/page-header";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { type ActiveBuInstance, useActiveBuInstances } from "@/hooks/use-bu-instances";
import {
  useClearLastPrivateHireSearch,
  useLastPrivateHireSearch,
  useSearchPrivateHires,
} from "@/hooks/use-private-hire-search";
import type { SearchPrivateHiresResult } from "@/lib/coach-manager/client";
import { privateHireSearchKeys } from "@/lib/query";
import type { PrivateHireSearchRequest } from "@/schemas/private-hires";

const OPERATOR_LABELS: Record<string, string> = {
  GreaterThan: "after",
  EqualTo: "on",
  LessThan: "before",
};

function buildCriteriaFields(
  criteria: PrivateHireSearchRequest,
  buInstances: ActiveBuInstance[] | undefined,
  buInstanceLabel: string,
): Array<{ label: string; value: string }> {
  const bu = buInstances?.find((b) => b.id === criteria.buInstanceId);
  const fields: Array<{ label: string; value: string }> = [
    { label: buInstanceLabel, value: bu?.name ?? criteria.buInstanceId },
  ];

  if (criteria.privateHireId != null) {
    fields.push({ label: "PrivateHire ID", value: String(criteria.privateHireId) });
  }
  if (criteria.privateHireMovementId != null) {
    fields.push({ label: "Movement ID", value: String(criteria.privateHireMovementId) });
  }
  if (criteria.clientId) fields.push({ label: "Client ID", value: criteria.clientId });
  if (criteria.surname) fields.push({ label: "Surname", value: criteria.surname });
  if (criteria.company) fields.push({ label: "Company", value: criteria.company });
  if (criteria.initials) fields.push({ label: "Initials", value: criteria.initials });
  if (criteria.pickup) fields.push({ label: "Pickup", value: criteria.pickup });
  if (criteria.destination) fields.push({ label: "Destination", value: criteria.destination });
  if (criteria.clientReference1) {
    fields.push({ label: "Client Reference 1", value: criteria.clientReference1 });
  }
  if (criteria.clientReference2) {
    fields.push({ label: "Client Reference 2", value: criteria.clientReference2 });
  }
  if (criteria.postCode) fields.push({ label: "Post Code", value: criteria.postCode });
  if (criteria.pickupDate) {
    const op = criteria.pickupDateOperator
      ? OPERATOR_LABELS[criteria.pickupDateOperator]
      : undefined;
    fields.push({
      label: "Pickup Date",
      value: op ? `${op} ${criteria.pickupDate}` : criteria.pickupDate,
    });
  }
  if (criteria.dateCreated) {
    const op = criteria.dateCreatedOperator
      ? OPERATOR_LABELS[criteria.dateCreatedOperator]
      : undefined;
    fields.push({
      label: "Date Created",
      value: op ? `${op} ${criteria.dateCreated}` : criteria.dateCreated,
    });
  }
  if (criteria.lastUpdated) {
    const op = criteria.lastUpdatedOperator
      ? OPERATOR_LABELS[criteria.lastUpdatedOperator]
      : undefined;
    fields.push({
      label: "Last Updated",
      value: op ? `${op} ${criteria.lastUpdated}` : criteria.lastUpdated,
    });
  }

  return fields;
}

export function PrivateHireSearchPage() {
  const queryClient = useQueryClient();
  const { label } = useBuInstanceLabel();
  const { data: buInstances } = useActiveBuInstances();
  const search = useSearchPrivateHires();
  const lastSearch = useLastPrivateHireSearch();
  const clearLastSearch = useClearLastPrivateHireSearch();

  function handleSearch(values: PrivateHireSearchRequest) {
    search.mutate(values, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: privateHireSearchKeys.last });
      },
    });
  }

  function handleClear() {
    clearLastSearch.mutate();
    search.reset();
  }

  // Bridges the gap between "mutation resolved" and "invalidated lastSearch
  // query refetching" so old results don't flash before the fresh ones land —
  // pure derived state, no extra useState.
  const isPending = search.isPending || (search.isSuccess && lastSearch.isFetching);

  const state = lastSearch.data;
  const result: SearchPrivateHiresResult | undefined = state
    ? state.status === "success"
      ? { ok: true, privateHires: state.privateHires ?? [], resultDescription: "" }
      : { ok: false, error: state.errorMessage ?? "Search failed" }
    : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="PrivateHire Search"
        description={`Search a Coach Manager ${label} for a PrivateHire and view its full detail.`}
      />
      <PrivateHireSearchForm onSearch={handleSearch} isSearching={search.isPending} />
      {state && !isPending && (
        <LastSearchBanner
          fields={buildCriteriaFields(state.criteria, buInstances, label)}
          searchedAt={state.searchedAt}
          onClear={handleClear}
          isClearing={clearLastSearch.isPending}
        />
      )}
      <PrivateHireSearchResults
        result={result}
        isPending={isPending}
        error={search.error ?? lastSearch.error}
      />
    </div>
  );
}
